import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Avatar,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Language as LanguageIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { customersAPI, proposalsAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import { format } from 'date-fns';

const CustomerView = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proposalsLoading, setProposalsLoading] = useState(true);

  useEffect(() => {
    fetchCustomer();
    fetchCustomerProposals();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getById(id);
      setCustomer(response.data.customer);
    } catch (error) {
      console.error('Error fetching customer:', error);
      toast.error('Failed to fetch customer details');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerProposals = async () => {
    try {
      setProposalsLoading(true);
      const response = await proposalsAPI.getAll({ customerId: id });
      setProposals(response.data.proposals || []);
    } catch (error) {
      console.error('Error fetching customer proposals:', error);
    } finally {
      setProposalsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return <LoadingSpinner message="Loading customer details..." />;
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Customer not found
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ pl: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={() => navigate('/customers')}
            sx={{
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {customer.companyName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Customer Details
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/customers/edit/${customer.id}`)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Edit Customer
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Customer Information */}
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    width: 64,
                    height: 64,
                    mr: 2
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {customer.companyName}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {customer.contactPerson}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <List sx={{ py: 0 }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <PersonIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Contact Person"
                        secondary={customer.contactPerson || 'N/A'}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <EmailIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={customer.email || 'N/A'}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <PhoneIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Phone"
                        secondary={customer.phone || 'N/A'}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <LanguageIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Website"
                        secondary={customer.website || 'N/A'}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <List sx={{ py: 0 }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <LocationIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Address"
                        secondary={
                          customer.address ? (
                            <Box component="span">
                              {customer.address}<br />
                              {customer.city && `${customer.city}, `}
                              {customer.state && `${customer.state} `}
                              {customer.zipCode}<br />
                              {customer.country}
                            </Box>
                          ) : 'N/A'
                        }
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CalendarIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Created"
                        secondary={formatDate(customer.createdAt)}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>

              {customer.notes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon color="action" />
                      Notes
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
                        borderRadius: 2
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {customer.notes}
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Stats & Proposals */}
        <Grid item xs={12} md={4}>
          {/* Stats Card */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Statistics
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2">Total Proposals</Typography>
                  <Chip
                    label={proposals.length}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2">Accepted</Typography>
                  <Chip
                    label={proposals.filter(p => p.status === 'accepted').length}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2">Pending</Typography>
                  <Chip
                    label={proposals.filter(p => p.status === 'pending').length}
                    color="warning"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Total Value</Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">
                    {formatCurrency(proposals.reduce((sum, p) => sum + (p.amount || 0), 0))}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Recent Proposals */}
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Recent Proposals
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/proposals', { state: { customerId: customer.id } })}
                  sx={{ textTransform: 'none' }}
                >
                  View All
                </Button>
              </Box>
              {proposalsLoading ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                  Loading proposals...
                </Typography>
              ) : proposals.length > 0 ? (
                <List sx={{ py: 0 }}>
                  {proposals.slice(0, 5).map((proposal, index) => (
                    <ListItem
                      key={proposal.id}
                      sx={{
                        px: 0,
                        borderBottom: index < Math.min(proposals.length, 5) - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: 1
                        }
                      }}
                      onClick={() => navigate(`/proposals/view/${proposal.id}`)}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {proposal.title}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={proposal.status}
                              color={getStatusColor(proposal.status)}
                              size="small"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(proposal.amount)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                  No proposals found
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerView;